import type { JSX } from "react";
import * as React from "react";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { Popover } from "@mui/material";
import "./editorClasses.css";
import CheckBoxMultiCheck, { CheckEditData } from "../../MainPage/Sections/ElementsSections/CheckBoxMultiCheck";
import { EventDataInfo } from "../../MainPage/Tools/VirtualList/VirtualList";

interface CustomCheckBoxListProps {
  items: EventDataInfo[];
  id?: string;
  open: boolean;
  anchorEl?: Element | null;
  handleClose: () => Promise<void>;
  onEditCustomCheckList: ((data: any) => void | Promise<void>) | undefined;
}

export default function CustomCheckBoxList(props: CustomCheckBoxListProps) {
  const { items, open, anchorEl, id, handleClose, onEditCustomCheckList } = props;
  const currentTarget = React.useRef<HTMLUListElement | null>(null);
  const listRef = React.useRef<HTMLUListElement | null>(null);
  const [itemsJsx, setItemsJsx] = React.useState<JSX.Element[]>([]);
  const checkstatesCount = React.useRef<Record<string, string>>({});
  React.useEffect(() => {
    setItemsJsx(
      items.map((value) => {
        const labelId = `checkbox-list-label-${value.id}`;

        return (
          <ListItem
            title={value.label}
            key={value.id}
            style={{
              borderBottom: "1px solid #eeeeee",
              height: "27px",
              minWidth: "200px",
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
            }}
            disablePadding
          >
            <ListItemIcon style={{ height: "inherit", minWidth: "0" }}>
              <CheckBoxMultiCheck
                parentProps={{ name: value.label, id: value.id }}
                CheckState={value.checkState}
                MultiCheckSet={value.MultiCheckSet}
                onEdit={(data) => {
                  getCountCheckStates(data);
                  onEditCustomCheckList?.({
                    ...data,
                    states: checkstatesCount.current,
                  });
                }}
              />
            </ListItemIcon>
            <ListItemText id={labelId} primary={value.label} />
          </ListItem>
        );
      }),
    );
  }, [items]);

  function getCountCheckStates(data: CheckEditData) {
    if (data.id === undefined) return;
    if (data.CheckState === "0") {
      delete checkstatesCount.current[data.id];
    } else {
      checkstatesCount.current[data.id] = data.CheckState;
    }
  }

  function arrowDown() {
    if (!listRef.current) return;
    let el: any = listRef.current.querySelector(".focusCheckListItem");
    const firstChild = listRef.current.firstChild;
    const lastChild = listRef.current.lastChild;

    if (!el) {
      el = firstChild;
      el.focus();
      el.classList.add("focusCheckListItem");
      currentTarget.current = el;
      return;
    }

    if (el === lastChild) {
      el.classList.remove("focusCheckListItem");
      el = firstChild;
      el.classList.add("focusCheckListItem");
      currentTarget.current = el;
    } else {
      el.classList.remove("focusCheckListItem");
      el = el.nextElementSibling;
      el.classList.add("focusCheckListItem");
      currentTarget.current = el;
    }

    const parentEl = listRef.current.parentElement?.parentElement;
    if (!parentEl) return;
    const scrollingPos = el.getBoundingClientRect().y - parentEl.getBoundingClientRect().y;

    parentEl.scrollBy({
      left: 0,
      top: scrollingPos,
      behavior: "smooth",
    });
  }

  function arrowUp() {
    if (!listRef.current) return;
    let el: any = listRef.current.querySelector(".focusCheckListItem");
    const firstChild = listRef.current.firstChild;
    const lastChild = listRef.current.lastChild;

    if (!el) {
      el = lastChild;
      el.classList.add("focusCheckListItem");
      currentTarget.current = el;
      return;
    }

    if (el === firstChild) {
      el.classList.remove("focusCheckListItem");
      el = lastChild;
      el.classList.add("focusCheckListItem");
      currentTarget.current = el;
    } else {
      el.classList.remove("focusCheckListItem");
      el = el.previousElementSibling;
      el.classList.add("focusCheckListItem");
      currentTarget.current = el;
    }

    const parentEl = listRef.current.parentElement?.parentElement;
    if (!parentEl) return;
    const scrollingPos = el.getBoundingClientRect().y - parentEl.getBoundingClientRect().y;

    parentEl.scrollBy({
      left: 0,
      top: scrollingPos,
      behavior: "smooth",
    });
  }

  function onKeyDown(ev: any) {
    switch (ev.code) {
      case "ArrowDown":
        arrowDown();
        break;
      case "ArrowUp":
        arrowUp();
        break;
      case "Space":
        ev.preventDefault();
        if (currentTarget.current) {
          const bttn: HTMLButtonElement | null = currentTarget.current.querySelector(`[listbutton="true"]`);
          bttn?.click();
        }
        break;
      case "Enter":
        ev.stopPropagation();
        break;
      case "Tab":
        ev.preventDefault();
        arrowDown();
        break;
    }
  }

  return (
    <Popover
      transitionDuration={50}
      onKeyDown={onKeyDown}
      id={id}
      open={open}
      anchorEl={anchorEl}
      onClose={() => {
        handleClose();
        checkstatesCount.current = {};
      }}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
    >
      <div
        ref={(el) => {
          if (!el) return;
          const listEl = el.querySelector("ul");
          listRef.current = listEl;
        }}
        checklist="true"
      >
        <List
          sx={{
            width: "fit-content",
            maxWidth: `${document.body.clientWidth / 2}px`,
            maxHeight: "400px",
            bgcolor: "background.paper",
          }}
        >
          {itemsJsx}
        </List>
      </div>
    </Popover>
  );
}
